import React from 'react';

export interface ExecutiveReportSnapshot {
  leadTimeMinutes: number;
  pcePercent: number;
  nvaCost: number;
  deliveryCapacityPerDay: number;
}

export interface ExecutiveReportTemplateData {
  simulationId: string;
  generatedAtIso: string;
  statusLabel: string;
  before: ExecutiveReportSnapshot;
  after: ExecutiveReportSnapshot;
  estimatedRoiPercent: number;
  projectedSavings: number;
  criticalResourceName: string;
  criticalLoadPercent: number;
  taktTimeMinutes: number;
  waitRemovedMinutes: number;
  variabilityMinutes: number;
}

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

const formatHours = (minutes: number) => `${(Math.max(0, minutes) / 60).toFixed(1)}h`;

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

const arrow = (value: number, betterWhenUp = true) => {
  if (value === 0) return '->';
  const isPositive = value > 0;
  if (betterWhenUp) {
    return isPositive ? '↑' : '↓';
  }
  return isPositive ? '↓' : '↑';
};

const deltaClass = (isPositive: boolean) => (isPositive ? '#16A34A' : '#DC2626');

export const ExecutiveReportTemplate: React.FC<{ data: ExecutiveReportTemplateData }> = ({ data }) => {
  const leadDelta = data.after.leadTimeMinutes - data.before.leadTimeMinutes;
  const leadReductionPercent =
    data.before.leadTimeMinutes > 0
      ? ((data.before.leadTimeMinutes - data.after.leadTimeMinutes) / data.before.leadTimeMinutes) * 100
      : 0;
  const pceDelta = data.after.pcePercent - data.before.pcePercent;
  const nvaDelta = data.after.nvaCost - data.before.nvaCost;
  const nvaReductionPercent = data.before.nvaCost > 0 ? ((data.before.nvaCost - data.after.nvaCost) / data.before.nvaCost) * 100 : 0;
  const capacityDelta = data.after.deliveryCapacityPerDay - data.before.deliveryCapacityPerDay;
  const capacityDeltaPercent =
    data.before.deliveryCapacityPerDay > 0
      ? ((data.after.deliveryCapacityPerDay - data.before.deliveryCapacityPerDay) / data.before.deliveryCapacityPerDay) * 100
      : 0;

  const tableHeader: React.CSSProperties = {
    fontSize: 11,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#475569',
    borderBottom: '1px solid #CBD5E1',
    padding: '8px 10px',
    textAlign: 'left',
  };

  const tableCell: React.CSSProperties = {
    borderBottom: '1px solid #E2E8F0',
    padding: '10px',
    fontSize: 12,
    color: '#1E293B',
  };

  return (
    <div
      style={{
        width: 1080,
        position: 'relative',
        background: '#FFFFFF',
        color: '#1E293B',
        fontFamily: 'Segoe UI, Arial, sans-serif',
        border: '1px solid #CBD5E1',
        boxSizing: 'border-box',
      }}
    >
      <header style={{ padding: '20px 24px', borderBottom: '1px solid #CBD5E1', background: '#F8FAFC' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '0.02em' }}>WORKFLOW MANAGER</div>
            <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>RELATORIO DE IMPACTO - OTIMIZACAO DE FLUXO E ROI</div>
            <div style={{ fontSize: 11, color: '#475569', marginTop: 8 }}>
              ID da Simulacao: <b style={{ fontFamily: 'Consolas, Menlo, monospace' }}>{data.simulationId}</b>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div
              style={{
                display: 'inline-block',
                border: '1px solid #16A34A',
                color: '#166534',
                background: '#ECFDF3',
                borderRadius: 4,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                padding: '4px 8px',
              }}
            >
              {data.statusLabel}
            </div>
            <div style={{ fontSize: 10, color: '#64748B', marginTop: 8 }}>
              Gerado em {new Date(data.generatedAtIso).toLocaleString('pt-BR')}
            </div>
          </div>
        </div>
      </header>

      <section style={{ padding: '16px 24px 12px' }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          1. Sumario de Ganhos e Performance
        </h3>
        <p style={{ margin: '8px 0 0', fontSize: 12, color: '#334155' }}>
          A implementacao das melhorias propostas resulta em ganho estrutural imediato na operacao.
        </p>
        <div style={{ marginTop: 8, fontSize: 12, color: '#1E293B', lineHeight: 1.7 }}>
          <div>
            Economia Direta Projetada: <b style={{ fontFamily: 'Consolas, Menlo, monospace' }}>{formatCurrency(data.projectedSavings)}</b>
          </div>
          <div>
            Reducao de Lead Time: <b style={{ fontFamily: 'Consolas, Menlo, monospace' }}>{formatPercent(leadReductionPercent)}</b>, de{' '}
            <b style={{ fontFamily: 'Consolas, Menlo, monospace' }}>{formatHours(data.before.leadTimeMinutes)}</b> para{' '}
            <b style={{ fontFamily: 'Consolas, Menlo, monospace' }}>{formatHours(data.after.leadTimeMinutes)}</b>.
          </div>
          <div>
            Eficiencia de Ciclo (PCE): de <b style={{ fontFamily: 'Consolas, Menlo, monospace' }}>{formatPercent(data.before.pcePercent)}</b> para{' '}
            <b style={{ fontFamily: 'Consolas, Menlo, monospace' }}>{formatPercent(data.after.pcePercent)}</b>.
          </div>
        </div>
      </section>

      <section style={{ padding: '8px 24px 12px' }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          2. Comparativo Tecnico (Estado Atual vs Futuro)
        </h3>
        <div style={{ marginTop: 10, border: '1px solid #CBD5E1' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#F8FAFC' }}>
              <tr>
                <th style={tableHeader}>Indicador Critico</th>
                <th style={tableHeader}>Estado Atual</th>
                <th style={tableHeader}>Projecao Otimizada</th>
                <th style={{ ...tableHeader, textAlign: 'right' }}>Impacto (Delta)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tableCell}>Lead Time Total</td>
                <td style={{ ...tableCell, fontFamily: 'Consolas, Menlo, monospace' }}>{formatHours(data.before.leadTimeMinutes)}</td>
                <td style={{ ...tableCell, fontFamily: 'Consolas, Menlo, monospace' }}>{formatHours(data.after.leadTimeMinutes)}</td>
                <td
                  style={{
                    ...tableCell,
                    textAlign: 'right',
                    color: deltaClass(leadDelta < 0),
                    fontWeight: 700,
                    fontFamily: 'Consolas, Menlo, monospace',
                  }}
                >
                  {leadDelta <= 0 ? '-' : '+'}
                  {formatPercent(Math.abs(leadReductionPercent))} {arrow(leadDelta, false)}
                </td>
              </tr>
              <tr>
                <td style={tableCell}>Eficiencia (PCE)</td>
                <td style={{ ...tableCell, fontFamily: 'Consolas, Menlo, monospace' }}>{formatPercent(data.before.pcePercent)}</td>
                <td style={{ ...tableCell, fontFamily: 'Consolas, Menlo, monospace' }}>{formatPercent(data.after.pcePercent)}</td>
                <td
                  style={{
                    ...tableCell,
                    textAlign: 'right',
                    color: deltaClass(pceDelta >= 0),
                    fontWeight: 700,
                    fontFamily: 'Consolas, Menlo, monospace',
                  }}
                >
                  {pceDelta >= 0 ? '+' : '-'}
                  {formatPercent(Math.abs(pceDelta))} {arrow(pceDelta, true)}
                </td>
              </tr>
              <tr>
                <td style={tableCell}>Custo de Espera (NVA)</td>
                <td style={{ ...tableCell, fontFamily: 'Consolas, Menlo, monospace' }}>{formatCurrency(data.before.nvaCost)}</td>
                <td style={{ ...tableCell, fontFamily: 'Consolas, Menlo, monospace' }}>{formatCurrency(data.after.nvaCost)}</td>
                <td
                  style={{
                    ...tableCell,
                    textAlign: 'right',
                    color: deltaClass(nvaDelta < 0),
                    fontWeight: 700,
                    fontFamily: 'Consolas, Menlo, monospace',
                  }}
                >
                  {nvaDelta <= 0 ? '-' : '+'}
                  {formatPercent(Math.abs(nvaReductionPercent))} {arrow(nvaDelta, false)}
                </td>
              </tr>
              <tr>
                <td style={tableCell}>Capacidade Diaria</td>
                <td style={{ ...tableCell, fontFamily: 'Consolas, Menlo, monospace' }}>
                  {Math.round(data.before.deliveryCapacityPerDay)}/dia
                </td>
                <td style={{ ...tableCell, fontFamily: 'Consolas, Menlo, monospace' }}>
                  {Math.round(data.after.deliveryCapacityPerDay)}/dia
                </td>
                <td
                  style={{
                    ...tableCell,
                    textAlign: 'right',
                    color: deltaClass(capacityDelta >= 0),
                    fontWeight: 700,
                    fontFamily: 'Consolas, Menlo, monospace',
                  }}
                >
                  {capacityDelta >= 0 ? '+' : '-'}
                  {formatPercent(Math.abs(capacityDeltaPercent))} {arrow(capacityDelta, true)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section style={{ padding: '8px 24px 22px' }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          3. Plano de Acao Sugerido (Auto-Kaizen)
        </h3>
        <div style={{ marginTop: 10, fontSize: 12, color: '#1E293B', lineHeight: 1.75 }}>
          <div>
            Balanceamento de Carga (Muri): ajuste no no <b>{data.criticalResourceName}</b>, reduzindo sobrecarga de{' '}
            <b style={{ fontFamily: 'Consolas, Menlo, monospace' }}>{formatPercent(data.criticalLoadPercent)}</b> para niveis aderentes ao Takt de{' '}
            <b style={{ fontFamily: 'Consolas, Menlo, monospace' }}>{data.taktTimeMinutes.toFixed(1)}m</b>.
          </div>
          <div>
            Eliminacao de Esperas (Muda): reducao de <b style={{ fontFamily: 'Consolas, Menlo, monospace' }}>{Math.round(data.waitRemovedMinutes)} min</b> de
            tempo sem agregacao de valor no fluxo.
          </div>
          <div>
            Estabilizacao de Fluxo: buffer operacional para absorver variabilidade de{' '}
            <b style={{ fontFamily: 'Consolas, Menlo, monospace' }}>{data.variabilityMinutes.toFixed(1)} min</b> no cenario probabilistico.
          </div>
        </div>
      </section>

      <footer
        style={{
          borderTop: '1px solid #CBD5E1',
          padding: '14px 24px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div style={{ fontSize: 12, color: '#334155' }}>
          Assinatura do Consultor: <span style={{ letterSpacing: '0.12em' }}>__________________________</span>
        </div>
        <div
          style={{
            fontSize: 10,
            color: '#0F172A',
            border: '1px solid #CBD5E1',
            padding: '5px 8px',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            background: '#F8FAFC',
            fontWeight: 700,
          }}
        >
          Workflow Manager AI - Simulacao de Precisao
        </div>
      </footer>

      <div
        style={{
          position: 'absolute',
          right: 18,
          top: 120,
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg)',
          fontSize: 10,
          letterSpacing: '0.12em',
          color: '#CBD5E1',
          textTransform: 'uppercase',
        }}
      >
        Simulacao validada via IA
      </div>
    </div>
  );
};

export default ExecutiveReportTemplate;
